import * as THREE from 'three';
import { Entity, type EntityCtx } from '../Entity';
import { Explosion } from './Explosion';
import { EnemyPlane } from './EnemyPlane';
import { Turret } from './Turret';
import { useStore } from '../../state/store';

const V = 15;
const DROP_INTERVAL = 6;

type DyingStage = 'alive' | 'ignition' | 'fireball' | 'falling' | 'impact' | 'grounded' | 'done';

export class Zeppelin extends Entity {
  angle: number;
  radius: number;
  altitude: number;
  forward = new THREE.Vector3(0, 0, -1);
  dropTimer = 0;
  dropsRemaining = 3; // cap so the wave can finish even if the zeppelin survives long
  dyingStage: DyingStage = 'alive';
  dieTimer = 0;
  groundedTimer = 0;
  groundSmokeTimer = 0;
  flashQuad?: THREE.Mesh;
  envelope: THREE.Group;

  constructor(pos: THREE.Vector3) {
    super('zeppelin');
    this.hp = 40;
    this.radius = 24;
    // Multi-part collision. Local +X runs along the long axis (envelope built scaled
    // (5,1,1.2) on a sphere of r=6 → ±30 X, ±6 Y, ±7.2 Z; gondolas hang below; fins aft).
    // The envelope is the bulk; gondolas + fins are smaller appendages, so bullets that
    // pass below the gasbag (where there's no gondola) now miss. All parts route to damage().
    this.hitParts = [
      { id: 'envelope',
        box: new THREE.Box3(new THREE.Vector3(-30, -2, -7.2), new THREE.Vector3(30, 7, 7.2)),
        object: this.group },
      { id: 'gondolas',
        box: new THREE.Box3(new THREE.Vector3(-13, -9, -3), new THREE.Vector3(9, -2, 3)),
        object: this.group },
      { id: 'fins',
        box: new THREE.Box3(new THREE.Vector3(-31, -4, -6), new THREE.Vector3(-23, 4, 6)),
        object: this.group },
    ];
    this.angle = Math.atan2(pos.z, pos.x);
    this.radius2 = Math.hypot(pos.x, pos.z);
    this.altitude = pos.y;
    this.group.position.copy(pos);
    this.envelope = makeZeppelinMesh();
    this.group.add(this.envelope);
    // Defensive gun gondolas hanging beneath the gasbag, fore and aft.
    for (const gx of [-9, 6]) {
      const t = new Turret({ range: 720, interval: 0.7, speed: 240, size: 2 });
      t.attachTo(this.group, gx, -8.5, 0);
      this.turrets.push(t);
    }
  }

  private turrets: Turret[] = [];

  // TS needs this type-safe; store the orbit radius under a different field to avoid shadowing `radius` (collision radius).
  private radius2: number = 0;

  update(ctx: EntityCtx): void {
    const { dt } = ctx;
    if (this.dyingStage !== 'alive') {
      this.updateDying(ctx);
      return;
    }

    // Orbit around origin at constant altitude
    this.angle += (V / this.radius2) * dt;
    const x = Math.cos(this.angle) * this.radius2;
    const z = Math.sin(this.angle) * this.radius2;
    this.group.position.set(x, this.altitude, z);
    // Face along tangent
    const tangentAngle = this.angle + Math.PI / 2;
    this.group.rotation.set(0, -tangentAngle, 0);

    // Defensive gondola guns track and fire on the player.
    for (const t of this.turrets) t.update(ctx, this.id);

    // Drop biplanes periodically — capped so the wave can advance.
    const activeEnemies = ctx.game.findEnemies().filter((e) => e.kind === 'enemy').length;
    this.dropTimer += dt;
    if (this.dropTimer >= DROP_INTERVAL && activeEnemies < 12 && this.dropsRemaining > 0) {
      this.dropTimer = 0;
      this.dropsRemaining--;
      const dropPos = this.group.position.clone();
      dropPos.y -= 10;
      const e = new EnemyPlane(dropPos);
      ctx.game.add(e);
    }
  }

  alive(): boolean { return this.dyingStage === 'alive'; }

  damage(amount: number, ctx: EntityCtx): void {
    if (this.dyingStage !== 'alive') return;
    this.hp -= amount;
    // Small impact puff
    new Explosion(this.randomHullPoint(), 6, 0.35).addTo(ctx);
    if (this.hp <= 0) this.beginDying(ctx);
  }

  private randomHullPoint(): THREE.Vector3 {
    const p = this.group.position.clone();
    const local = new THREE.Vector3(
      (Math.random() - 0.5) * 60,
      (Math.random() - 0.5) * 10,
      (Math.random() - 0.5) * 14,
    );
    local.applyQuaternion(this.group.quaternion);
    p.add(local);
    return p;
  }

  private beginDying(ctx: EntityCtx) {
    this.dyingStage = 'ignition';
    this.dieTimer = 0;
    useStore.getState().registerKill(500);
    // Initial ignition puff + the long hydrogen roar audio.
    new Explosion(this.randomHullPoint(), 12, 0.4).addTo(ctx);
    ctx.game.audio.playHydrogenRoar();
  }

  private updateDying(ctx: EntityCtx) {
    this.dieTimer += ctx.dt;
    // Stage transitions
    if (this.dyingStage === 'ignition' && this.dieTimer >= 0.1) {
      this.dyingStage = 'fireball';
      new Explosion(this.group.position.clone(), 90, 1.0).addTo(ctx);
      this.spawnScreenFlash(ctx);
    }
    if (this.dyingStage === 'fireball' && this.dieTimer >= 0.3) {
      this.dyingStage = 'falling';
    }
    if (this.dyingStage === 'falling') {
      this.group.rotation.x -= 0.6 * ctx.dt;
      this.group.position.y -= (40 + this.dieTimer * 20) * ctx.dt;
      if (Math.random() < 0.6) new Explosion(this.randomHullPoint(), 10, 0.5).addTo(ctx);
      for (const off of [-1, 1]) {
        if (Math.random() < 0.85) {
          const pt = this.randomHullPoint();
          pt.x += off * 4;
          new Explosion(pt, 28, 3.2, 'smoke')
            .setDrift(new THREE.Vector3(0, 12, 0))
            .addTo(ctx);
        }
      }
      const crashY = ctx.game.gameScene.heightAt(this.group.position.x, this.group.position.z);
      if (this.group.position.y < crashY) {
        this.dyingStage = 'impact';
        this.group.position.y = crashY;
        new Explosion(this.group.position.clone().setY(crashY - 10), 180, 1.4, 'fire').addTo(ctx);
        new Explosion(this.group.position.clone().setY(crashY), 90, 5, 'smoke')
          .setDrift(new THREE.Vector3(0, 15, 0))
          .addTo(ctx);
        // A burning airship flattens a wide swathe of forest, buildings and anything beneath it.
        ctx.game.crashImpact(ctx, this.group.position.clone().setY(crashY), 140, 24, this);
      }
    }
    if (this.dyingStage === 'impact') {
      // Frozen on the ground — stop all motion and transition to a lingering smoke plume.
      this.dyingStage = 'grounded';
      this.groundedTimer = 0;
      this.groundSmokeTimer = 0;
    }
    if (this.dyingStage === 'grounded') {
      this.groundedTimer += ctx.dt;
      this.groundSmokeTimer += ctx.dt;
      if (this.groundSmokeTimer >= 0.4) {
        this.groundSmokeTimer = 0;
        // A few rising plumes from the debris field
        for (let i = 0; i < 2; i++) {
          new Explosion(
            this.group.position.clone().add(new THREE.Vector3((Math.random() - 0.5) * 30, 4, (Math.random() - 0.5) * 30)),
            35,
            5,
            'smoke',
          ).setDrift(new THREE.Vector3(0, 12, 0)).addTo(ctx);
        }
      }
      if (this.groundedTimer > 30) this.dyingStage = 'done';
    }
    if (this.dyingStage === 'done') {
      this.dead = true;
    }
  }

  private spawnScreenFlash(ctx: EntityCtx) {
    // Big additive white quad parented to the camera for a 0.6s whiteout that fades.
    const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.85, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending });
    const geom = new THREE.PlaneGeometry(2, 2);
    const quad = new THREE.Mesh(geom, mat);
    quad.renderOrder = 999;
    const cam = ctx.game.camera;
    cam.add(quad);
    quad.position.set(0, 0, -0.6);
    quad.frustumCulled = false;
    const duration = 0.6;
    let t = 0;
    const tick = () => {
      t += 1 / 60;
      const k = Math.max(0, 1 - t / duration);
      mat.opacity = 0.85 * k;
      if (k <= 0) {
        quad.removeFromParent();
        mat.dispose();
        geom.dispose();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
}

function makeZeppelinMesh(): THREE.Group {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color: 0xbfb7a0, roughness: 0.95 });
  // Envelope: elongated ellipsoid approximated by a scaled sphere (4x segments)
  const env = new THREE.Mesh(new THREE.SphereGeometry(6, 80, 48), mat);
  env.scale.set(5, 1, 1.2);
  g.add(env);

  // Fins at tail
  const finMat = new THREE.MeshStandardMaterial({ color: 0x8c8576, roughness: 0.9 });
  const fin1 = new THREE.Mesh(new THREE.BoxGeometry(4, 4, 0.3, 8, 8, 2), finMat);
  fin1.position.set(-27, 2, 0);
  g.add(fin1);
  const fin2 = new THREE.Mesh(new THREE.BoxGeometry(4, 0.3, 4, 8, 2, 8), finMat);
  fin2.position.set(-27, 0, 0);
  g.add(fin2);

  // Gondolas
  const gondMat = new THREE.MeshStandardMaterial({ color: 0x5a4a36, roughness: 0.9 });
  for (const dx of [-10, 6]) {
    const gond = new THREE.Mesh(new THREE.BoxGeometry(6, 2, 3, 12, 4, 6), gondMat);
    gond.position.set(dx, -7, 0);
    g.add(gond);
  }

  // Identification band (dark cross hint — stylized, not historically literal)
  const bandMat = new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.9 });
  const band = new THREE.Mesh(new THREE.BoxGeometry(4, 2.2, 4, 8, 4, 8), bandMat);
  band.position.set(-18, 6.5, 0);
  g.add(band);

  g.traverse((o) => { (o as THREE.Mesh).castShadow = false; });
  return g;
}
